import React, { useMemo, Children } from 'react';
import { motion } from 'framer-motion';
import { photos } from '../data/photos';
interface FinalScreenProps {
  onRestart: () => void;
}
export const FinalScreen: React.FC<FinalScreenProps> = ({
  onRestart
}) => {
  // ============================================
  // === EDITEAZĂ MESAJUL FINAL PRINCIPAL AICI ===
  // ============================================
  const message = 'La mulți ani, prietene!';
  // ============================================
  // === EDITEAZĂ MESAJUL PERSONAL AICI ==========
  // ============================================
  const subMessage = 'Să ne vedem cu bine și la anul, la fel de tineri și cu mai multe povești adunate.';
  // ============================================
  // === EDITEAZĂ TEXTUL DE RESTART AICI =========
  // ============================================
  const restartLabel = 'Mai vezi o dată';
  // Pick photos for the floating thumbnails — distribute around the edges (avoiding center)
  const floatingPhotos = useMemo(() => {
    // Pick up to 12 photos
    const sampleSize = Math.min(12, photos.length);
    const step = Math.floor(photos.length / sampleSize);
    const picked = Array.from({
      length: sampleSize
    }, (_, i) => photos[i * step]);
    // Position around a ring with random jitter, avoiding the center
    return picked.map((photo, i) => {
      const angle = i / sampleSize * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
      // Radius in vmin (responsive)
      const radius = 32 + Math.random() * 14; // 32–46 vmin from center
      const x = 50 + Math.cos(angle) * radius; // % from left
      const y = 50 + Math.sin(angle) * radius; // % from top
      return {
        id: photo.id,
        src: photo.src,
        size: 56 + Math.random() * 24,
        rotation: Math.random() * 30 - 15,
        x,
        y,
        floatDuration: 8 + Math.random() * 6,
        floatDelay: Math.random() * 4,
        appearDelay: 0.6 + i * 0.12,
        floatDistance: 10 + Math.random() * 12
      };
    });
  }, []);
  // Sparkles
  const sparkles = useMemo(() => Array.from({
    length: 12
  }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 3 + 4,
    delay: Math.random() * 2
  })), []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07,
        delayChildren: 0.4
      }
    }
  };
  const letterVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        damping: 12,
        stiffness: 200
      }
    }
  };
  return <motion.div className="relative w-full h-full flex flex-col items-center justify-center px-6 text-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 1
  }} data-id="element-22">
      {/* Floating photo thumbnails — drift around the edges */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-23">
        {floatingPhotos.map(p => <motion.div key={p.id} className="absolute" style={{
        left: `${p.x}%`,
        top: `${p.y}%`,
        width: p.size,
        height: p.size,
        transform: 'translate(-50%, -50%)'
      }} initial={{
        opacity: 0,
        scale: 0.6
      }} animate={{
        opacity: [0, 0.45, 0.45, 0.45],
        scale: [0.6, 1, 1, 1],
        y: [0, -p.floatDistance, 0]
      }} transition={{
        opacity: {
          duration: 2,
          delay: p.appearDelay,
          times: [0, 0.4, 0.8, 1]
        },
        scale: {
          duration: 2,
          delay: p.appearDelay,
          times: [0, 0.4, 0.8, 1]
        },
        y: {
          duration: p.floatDuration,
          delay: p.floatDelay,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      }} data-id="element-24">
            <div className="w-full h-full overflow-hidden rounded-sm bg-cream" style={{
          transform: `rotate(${p.rotation}deg)`,
          padding: 3,
          boxShadow: '0 6px 16px -4px rgba(0,0,0,0.45)'
        }} data-id="element-25">
              <img src={p.src} alt="" aria-hidden="true" className="w-full h-full object-cover" draggable={false} data-id="element-26" />
            </div>
          </motion.div>)}
      </div>

      {/* Floating silver sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-27">
        {sparkles.map(sparkle => <motion.div key={sparkle.id} className="absolute bg-silver rounded-full" style={{
        left: `${sparkle.x}%`,
        top: `${sparkle.y}%`,
        width: sparkle.size,
        height: sparkle.size
      }} animate={{
        y: [0, -30, 0],
        opacity: [0.1, 0.6, 0.1]
      }} transition={{
        duration: sparkle.duration,
        repeat: Infinity,
        delay: sparkle.delay,
        ease: 'easeInOut'
      }} data-id="element-28" />)}
      </div>

      {/* Main message — animated letter by letter */}
      <motion.div className="font-marker font-bold text-cream text-4xl sm:text-6xl md:text-7xl mb-8 z-10 flex flex-wrap justify-center gap-x-2 sm:gap-x-4 leading-tight relative" variants={containerVariants} initial="hidden" animate="visible" data-id="element-29">
        {message.split(' ').map((word, wordIndex) => <div key={wordIndex} className="flex" data-id="element-30">
            {word.split('').map((char, charIndex) => <motion.span key={`${wordIndex}-${charIndex}`} variants={letterVariants} data-id="element-31">
                {char}
              </motion.span>)}
          </div>)}
      </motion.div>

      {/* Sub-message */}
      <motion.div className="font-inter text-silver text-base sm:text-xl max-w-md z-10 leading-relaxed mb-16 px-2 relative" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 2.2,
      duration: 0.8
    }} data-id="element-32">
        {subMessage}
      </motion.div>

      {/* Restart link */}
      <motion.button onClick={onRestart} className="font-inter text-silver/60 hover:text-silver text-xs sm:text-sm uppercase tracking-widest transition-colors z-10 pb-1 border-b border-silver/30 hover:border-silver relative" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 3.2,
      duration: 1
    }} data-id="element-33">
        {restartLabel}
      </motion.button>
    </motion.div>;
};